import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  /** Cards collapsed → render as a thin "show cards" tab instead of a draggable line. */
  collapsed: boolean
  /** Fires repeatedly during mouse drag with the cursor's absolute clientX. */
  onDragMove: (clientX: number) => void
  /** Fires from arrow keys. delta in px, positive = wider discussion. */
  onKeyStep: (deltaPx: number) => void
  /** Double-click → reset to default width. */
  onReset: () => void
  /** Click the tab while collapsed → bring cards back. */
  onUncollapse: () => void
}

export function ColumnResizer({
  collapsed,
  onDragMove,
  onKeyStep,
  onReset,
  onUncollapse,
}: Props) {
  const { t } = useTranslation()
  const [dragging, setDragging] = useState(false)
  const [hover, setHover] = useState(false)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!dragging) return
    const onMove = (e: MouseEvent) => onDragMove(e.clientX)
    const onUp = () => {
      setDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragging, onDragMove])

  if (collapsed) {
    return (
      <button
        onClick={onUncollapse}
        title={t('columnResizer.showCards')}
        aria-label={t('columnResizer.showCards')}
        style={{
          width: 14,
          flexShrink: 0,
          background: 'var(--paper-0)',
          borderLeft: '1px solid var(--rule)',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--ink-3)',
          fontSize: 13,
          lineHeight: 1,
          height: '100%',
        }}
        onMouseEnter={(e) => {
          ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--paper-1)'
          ;(e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-1)'
        }}
        onMouseLeave={(e) => {
          ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--paper-0)'
          ;(e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-3)'
        }}
      >
        ‹
      </button>
    )
  }

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    const SMALL = 16
    const LARGE = 64
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      onKeyStep(e.shiftKey ? -LARGE : -SMALL)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      onKeyStep(e.shiftKey ? LARGE : SMALL)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      onReset()
    }
  }

  const active = dragging || focused
  const lineWidth = active ? 2 : 1
  const lineColor = active
    ? 'var(--blue)'
    : hover
    ? 'var(--rule-s)'
    : 'var(--rule)'

  return (
    <div
      onMouseDown={onMouseDown}
      onDoubleClick={onReset}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('columnResizer.resizeHandle')}
      style={{
        width: 8,
        flexShrink: 0,
        cursor: 'col-resize',
        position: 'relative',
        background: 'transparent',
        outline: 'none',
        height: '100%',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: lineWidth,
          background: lineColor,
          transition: 'width 150ms ease-out, background 150ms ease-out',
          pointerEvents: 'none',
        }}
      />
    </div>
  )
}
